import React, { ReactElement, useState, useRef, useEffect } from 'react';
import { Form, Input, Message, Modal } from '@b-design/fusion';
import Button, { FormSubmit, promisifyValidate } from '@/components/Button';
import './index.less';
import store from '@/store';
import type { LoginParams } from '@/interfaces/user';
import { login, fetchUserInfo, getSendCode, setVerification } from '@/services/user';
import { useAuth, useLocation } from 'ice';
import { handleRedirect } from '@/utils';
import { LOGO as loginImg, isPublicDeploy } from '@/utils/config';
import $i18n from '@/i18n';
import { Field } from '@alifd/next';

interface LoginFormProps {
  children: ReactElement;
}

const LoginForm: React.FC<LoginFormProps> = (props) => {
  const field = Field.useField();
  const { init, validate, getError } = field;
  const [, userDispatcher] = store.useModel('user');
  const [, spaceRoleDispatcher] = store.useModel('spaceRole');
  const [showCode, setShowCode] = useState<any>({});
  const [codeModalVisible, setCodeModalVisible] = useState(false);
  const [emailCodeValue, setEmailCodeValue] = useState<Record<string, string | null>>({
    mail: null,
    verificationCode: null,
  });
  const [, setAuth] = useAuth();
  const location = useLocation();
  const resultRef = useRef<any>(null);

  async function updateUserInfo() {
    const userInfo = await fetchUserInfo();

    userDispatcher.updateCurrentUser(userInfo);
    return {
      _spaceId: userInfo?.defaultSpace?.spaceId,
      isAliyunPaymentUser: userInfo?.isAliyunPaymentUser,
      isDuibaUser: userInfo?.isDuibaUser,
      status: userInfo?.status,
      accountSrc: userInfo?.accountSrc,
    };
  }

  useEffect(() => {
    let params = new URLSearchParams(location.search);
    const safetyModalVisible = params.get('safetyModalVisible') || false;
    setCodeModalVisible(safetyModalVisible as boolean);
  }, []);

  async function handleSubmit(values: LoginParams) {
    const valid =
      values.username &&
      values.password &&
      (showCode?.validateCodeKey ? values?.validateCode : true);
    if (valid) {
      values.validateCodeKey = showCode?.validateCodeKey;
      try {
        const result = await login(values);
        setShowCode({
          validateCodeImage: result?.validateCodeImage,
          validateCodeKey: result?.validateCodeKey,
        });
        if (result.success) {
          const obj = await updateUserInfo();
          if (obj?.status === 4) {
            setEmailCodeValue({
              ...emailCodeValue,
              mail: values?.username,
            });
            setCodeModalVisible(true);
            resultRef.current = obj;
          } else {
            Message.success($i18n.get({ id: 'aopf.pages.login.LoginSucceeded', dm: '登录成功！' }));
            setAuth({
              admin: result.userType === 'admin',
              user: result.userType === 'user',
            });
            const { _spaceId, isDuibaUser } = obj;
            handleRedirect(_spaceId, isDuibaUser, spaceRoleDispatcher);
          }
        }
        // 如果失败去设置用户错误信息，显示提示信息
      } catch (error) {
        Message.error(
          $i18n.get({ id: 'aopf.pages.login.LoginFailedPleaseTryAgain', dm: '登录失败，请重试！' }),
        );
      }
    }
  }

  const handleModalSubmit = async () => {
    if (!emailCodeValue?.verificationCode) {
      Message.warning(
        $i18n.get({
          id: 'aopf.pages.login.registerForm.require.verificationCode',
          dm: '请输入验证码',
        }),
      );
      return;
    }
    const value = await setVerification({ ...emailCodeValue });
    if (!!value) {
      Message.success($i18n.get({ id: 'aopf.pages.login.LoginSucceeded', dm: '登录成功！' }));
      const { _spaceId, isDuibaUser } = resultRef.current || {};
      handleModalCancel();
      handleRedirect(_spaceId, isDuibaUser, spaceRoleDispatcher);
      return;
    }
    Message.error(
      $i18n.get({
        id: 'aopf.component.loginForms.VerificationFailedPleaseTryAgain',
        dm: '验证失败，请重试！',
      }),
    );
  };

  const handleModalCancel = () => {
    setCodeModalVisible(false);
  };

  const handleUpdateImg = async () => {
    const obj = await getSendCode({ validateCodeKey: showCode?.validateCodeKey });
    setShowCode({
      validateCodeImage: obj?.validateCodeImage,
      validateCodeKey: obj?.validateCodeKey,
    });
  };

  const handleEmailCodeChange = (value) => {
    setEmailCodeValue({
      ...emailCodeValue,
      verificationCode: value,
    });
  };

  const onValidate = async () => {
    const [errs, vals] = await promisifyValidate(field);
    if (errs) {
      return;
    }
    await handleSubmit(vals);
  };

  return (
    <div className="login-form">
      <div className="login-text">
        {$i18n.get({
          id: 'aopf.pages.login.form.title',
          dm: '登录',
        })}
      </div>
      <div className="login-icon">
        <img src={loginImg} style={{ width: 'auto', height: '100%' }} />
      </div>
      <Form style={{ maxWidth: '392px', margin: '0 auto', marginTop: '30px' }}>
        <Form.Item
          name="username"
          label={$i18n.get({
            id: 'aopf.pages.login.loginForm.label.account',
            dm: '账号',
          })}
          required
        >
          <Input
            placeholder="example@email.com"
            maxLength={64}
            showLimitHint
            {...init('username', {
              initValue: '',
              rules: {
                required: true,
                message: $i18n.get({
                  id: 'aopf.pages.login.loginForm.username.required',
                  dm: '请输入账号！',
                }),
              },
            })}
            onPressEnter={onValidate}
          />
          <p style={{ fontSize: 12, color: '#c82727', lineHeight: '18px', marginTop: '4px' }}>
            {getError('username')}
          </p>
        </Form.Item>

        <Form.Item
          name="password"
          label={$i18n.get({
            id: 'aopf.pages.login.loginForm.label.password',
            dm: '密码',
          })}
          required
        >
          <Input.Password
            placeholder={$i18n.get({
              id: 'aopf.pages.login.loginPasswordNotice',
              dm: '请输入您的密码',
            })}
            {...init('password', {
              initValue: '',
              rules: {
                required: true,
                message: $i18n.get({
                  id: 'aopf.pages.login.PleaseEnterThePassword',
                  dm: '请输入您的密码',
                }),
              },
            })}
            onPressEnter={onValidate}
          />

          {props.children}
          <p style={{ fontSize: 12, color: '#c82727', lineHeight: '18px', marginTop: '4px' }}>
            {getError('password')}
          </p>
        </Form.Item>
        {isPublicDeploy() && !!showCode?.validateCodeImage && (
          <div className="codeContent">
            <Form.Item
              name="validateCode"
              label={$i18n.get({
                id: 'aopf.pages.login.verificationCode',
                dm: '验证码',
              })}
              required
              style={{ flex: '1' }}
            >
              <Input
                placeholder={$i18n.get({
                  id: 'aopf.pages.login.loginPlaceEmailNotice',
                  dm: '请输入动态验证码',
                })}
                {...init('validateCode', {
                  initValue: '',
                  rules: {
                    required: true,
                    message: $i18n.get({
                      id: 'aopf.pages.login.PleaseEnterTheCode',
                      dm: '请输入您的验证码',
                    }),
                  },
                })}
                onPressEnter={onValidate}
              />
              <p style={{ fontSize: 12, color: '#c82727', lineHeight: '18px', marginTop: '4px' }}>
                {getError('validateCode')}
              </p>
            </Form.Item>
            <img
              src={showCode?.validateCodeImage}
              alt=""
              className="codeImg"
              onClick={handleUpdateImg}
            />
          </div>
        )}

        <Form.Item name="agree">
          <FormSubmit
            type="primary"
            // onClick={async (values, errors) => {
            //   if (errors) return;
            //   await handleSubmit(values as LoginParams);
            // }}
            onClick={onValidate}
            validate
            style={{
              height: 40,
              width: 392,
              borderRadius: 4,
              fontWeight: 500,
              fontSize: 16,
              color: '#fff',
              letterSpacing: '0.15px',
              lineHeight: '24px',
              position: 'absolute',
              bottom: 0,
            }}
          >
            {$i18n.get({
              id: 'aopf.pages.login.loginForm.login',
              dm: '登录',
            })}
          </FormSubmit>
        </Form.Item>
      </Form>
      <Modal
        width={$i18n.language === 'zh-CN' ? 400 : 500}
        v2
        closeIcon
        wrapperClassName="login-form-codeModal"
        visible={codeModalVisible}
        title={$i18n.get({
          id: 'aopf.pages.login.loginModaltitle',
          dm: '请输入动态验证码',
        })}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        footer={[
          <div className="dialog-footer">
            <Button
              type="primary"
              onClick={() => handleModalSubmit()}
              style={{ marginRight: '8px' }}
            >
              {$i18n.get({ id: 'aopf.component.invite.Ok', dm: '确定' })}
            </Button>
            <Button className="dialog-btn" type="normal" onClick={() => handleModalCancel()}>
              {$i18n.get({ id: 'aopf.component.invite.Cancel', dm: '取消' })}
            </Button>
          </div>,
        ]}
      >
        <Input
          placeholder={$i18n.get({
            id: 'aopf.pages.login.loginPlaceEmailNotice',
            dm: '请输入动态验证码',
          })}
          onPressEnter={handleModalSubmit}
          onChange={handleEmailCodeChange}
        />
      </Modal>
    </div>
  );
};

export default LoginForm;
